/**
 * 🏠 **The vefstofa as a pit house — D135, T289.**
 *
 * 🎯 **haugur, 2026-10-05**, choosing one question at a time: clothes are made
 * in the vefstofa, and the vefstofa becomes **a room you walk into** — *"a real
 * pit house"*, as every weaving house of 870–950 was (Milek 2012): timber walls
 * in a pit a metre deep, a turf roof, a stone oven in a corner.
 *
 * ## 🔴 What you see is what stops you
 *
 * Every wall, the ceiling, the step, the oven and the bench is a BLOCK. The
 * client draws the wall faces exactly on the block faces (`pithousemesh.ts`),
 * and only the roof, oven, bench and door are models sitting ON blocks.
 * `06_Traps.md` records a board hung 39 cm off a wall and a sheep in the air
 * over a roof, both from **a model's wall and a block's face being two
 * surfaces** — so here there is one.
 *
 * ## The frame — the búr's, reused
 *
 * The door's LOWER cell is the anchor and carries the `facing`; every other cell
 * is derived (`Minecraft/02` §4.1, the búr's rule). In house coordinates:
 *
 * | | |
 * |---|---|
 * | `w` | −3…+3 across the front, the door at 0 — `burFrame`'s `right`, which is **the player's LEFT as they walk in** (see `floorRole`) |
 * | `d` | 0…5 back from the front wall |
 * | `h` | up from the ground: `h = 0` is the first air above it, the door's own level |
 *
 * The wall ring is `d ∈ {0, 5}` or `|w| = 3`, at `h = −1…0`; the room is the
 * 5 × 4 inside it, dug to `h = −1`, 2 m high; the ceiling covers the 7 × 6 at
 * `h = +1`, the door's upper half sits in that row, and over the way in it is
 * raised to `h = +2`.
 * 📖 `Development/16_Clothes_And_The_Vefstofa.md` §3.
 */

import { blockIdForKey } from './blocks.ts';
import {
  BUR_FACINGS,
  burFrame,
  PIT_BENCH,
  PIT_DOOR,
  PIT_FLOOR,
  PIT_OVEN,
  PIT_PART,
  PIT_STEP,
  type BurFacing,
} from './bur.ts';
import { baseBlockOf, stateOf, withState } from './states.ts';

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** What placing writes into a cell. `dug` is a room cell at `h = −1` that becomes air. */
export type PitRole =
  'wall' | 'doorUpper' | 'ceiling' | 'step' | 'oven' | 'bench' | 'dug' | 'floor';

export interface PitCell extends Cell {
  readonly role: PitRole;
}

/** 📐 Half the width across, and the depth back: 7 × 6 outside. */
const HALF_WIDTH = 3;
const DEPTH = 6;
/**
 * 🧱 Walls run from the pit floor's level to here — 🎯 haugur: *"low, as in the
 * table"*: 2 m inside, so ONE metre of wall shows above the ground, as the real
 * pit houses sat half in the earth.
 */
const WALL_TOP = 0;
/** 🪵 The ceiling's row — the roof model sits on top of it. */
const CEILING = 1;
/** 🏔️ How high the plot must be clear: the roof model sits on the ceiling (top at h = +2) and stands about 1.5 m. */
export const ROOF_CLEAR = 3;

/**
 * 🚪 **The way in — the cell just inside the door.** The floor is a metre down
 * and you drop into it; the game climbs a full block, so you walk back up.
 * 🔴 **The ceiling is raised one block over it** (a cap at h = +2): with a 2 m
 * room, your head would otherwise hit the ceiling the moment you stepped in off
 * the threshold. ⚠️ **No step**: on a half-metre step under a 2 m ceiling your
 * head is IN the ceiling row, and you could not walk off it.
 */
const isWayIn = (d: number, w: number): boolean => d === 1 && w === 0;

const isRing = (d: number, w: number): boolean =>
  d === 0 || d === DEPTH - 1 || Math.abs(w) === HALF_WIDTH;

/** 📐 A cell in house coordinates. Exported for the loom (`loom.ts`), which stands on the back row. */
export function pitCellAt(door: Cell, facing: BurFacing, d: number, w: number, h: number): Cell {
  return cellAt(door, facing, d, w, h);
}

function cellAt(door: Cell, facing: BurFacing, d: number, w: number, h: number): Cell {
  const { into, right } = burFrame(facing);
  return {
    x: door.x + into.x * d + right.x * w,
    y: door.y + h,
    z: door.z + into.z * d + right.z * w,
  };
}

/**
 * 🪑 What stands on the room's floor row, by `(d, w)` — or `dug` for open floor.
 *
 * 🔴 **`burFrame`'s `right` (+w) is the player's LEFT as they walk in** — facing
 * into the house along `into`, a right-handed world puts their right hand at
 * −w. So the oven, agreed for the **back-right** corner, is at w = −2, and the
 * bench, agreed for the **left** wall, at w = +2. ⚠️ The first bake drew them
 * the other way round.
 */
function floorRole(d: number, w: number): PitRole {
  if (d === 4 && w === -2) return 'oven';
  if (w === 2) return 'bench';
  return 'dug';
}

/**
 * 🏠 **Every cell placing writes, except the anchor** — walls, the door's upper
 * half, the ceiling, and the room's floor row (step, oven, bench, or dug out).
 */
export function pitCells(door: Cell, facing: BurFacing): PitCell[] {
  const cells: PitCell[] = [];
  for (let d = 0; d < DEPTH; d++)
    for (let w = -HALF_WIDTH; w <= HALF_WIDTH; w++) {
      if (isRing(d, w)) {
        for (let h = -1; h <= WALL_TOP; h++) {
          if (d === 0 && w === 0 && h === 0) continue;
          cells.push({ ...cellAt(door, facing, d, w, h), role: 'wall' });
        }
      } else {
        cells.push({ ...cellAt(door, facing, d, w, -1), role: floorRole(d, w) });
        /* 🟫 Beaten earth under the room, not the lawn the ground would show. */
        cells.push({ ...cellAt(door, facing, d, w, -2), role: 'floor' });
      }
      /* 🪵 The ceiling row: the door's upper half sits IN it, and over the way
         in it is open, with the cap one higher. */
      if (d === 0 && w === 0)
        cells.push({ ...cellAt(door, facing, d, w, CEILING), role: 'doorUpper' });
      else if (isWayIn(d, w))
        cells.push({ ...cellAt(door, facing, d, w, CEILING + 1), role: 'ceiling' });
      else cells.push({ ...cellAt(door, facing, d, w, CEILING), role: 'ceiling' });
    }
  return cells;
}

/** 🟫 The ground the building stands in — `h = −1` under all 42 columns. */
export function pitGround(door: Cell, facing: BurFacing): Cell[] {
  const cells: Cell[] = [];
  for (let d = 0; d < DEPTH; d++)
    for (let w = -HALF_WIDTH; w <= HALF_WIDTH; w++) cells.push(cellAt(door, facing, d, w, -1));
  return cells;
}

/** 🟫 The pit's floor — `h = −2` under the 20 room columns. Refilling copies it upward. */
export function pitFloor(door: Cell, facing: BurFacing): Cell[] {
  const cells: Cell[] = [];
  for (let d = 0; d < DEPTH; d++)
    for (let w = -HALF_WIDTH; w <= HALF_WIDTH; w++)
      if (!isRing(d, w)) cells.push(cellAt(door, facing, d, w, -2));
  return cells;
}

/** 🚶 The room's open air — `h = −1…+1` inside, minus the step, oven and bench. */
export function pitRoom(door: Cell, facing: BurFacing): Cell[] {
  const cells: Cell[] = [];
  for (let d = 0; d < DEPTH; d++)
    for (let w = -HALF_WIDTH; w <= HALF_WIDTH; w++) {
      if (isRing(d, w)) continue;
      for (let h = -1; h <= WALL_TOP; h++) {
        if (h === -1 && floorRole(d, w) !== 'dug') continue;
        cells.push(cellAt(door, facing, d, w, h));
      }
      /* The open air under the cap is room too. */
      if (isWayIn(d, w)) cells.push(cellAt(door, facing, d, w, CEILING));
    }
  return cells;
}

/**
 * 👻 **Is this plot free and solid?** The client's shadow and the server's
 * refusal ask this one function, so they cannot disagree (the búr's rule).
 *
 * - `blocked` — the anchor, or anything from ground level to the top of the
 *   roof (`h = 0…+3`) over the 7 × 6, is not air.
 * - `built` — a ground or floor cell holds something the dig must not
 *   destroy: a chest, a bed, a part of another house (`diggable`).
 * - `unlevel` — a column's ground (`h = −1`) is air, not solid, or a fluid; or
 *   the room's floor (`h = −2`), or what it rests on (`h = −3`), is. 🔴 **A
 *   pit dug into the sea is a pond**, which is why a fluid is refused even
 *   where it is "solid" — and why a plank pier over the sea is refused too.
 *
 * 🔴 **#856 — it used to call ground `built` unless it matched the block 3 m
 * below.** That holds only where the surface is exactly level with the door:
 * one metre higher, and 3 m down is already móberg, so ordinary bumpy scrub
 * was refused as "built on". ✅ **Under the house, the dig takes whatever is
 * there** — haugur's clearing rule, one metre down — and the takedown puts
 * back what the generator made (`openNaturalGround`, server).
 */
export function pitPlotVerdict(
  door: Cell,
  facing: BurFacing,
  blockAt: (x: number, y: number, z: number) => number,
  isSolid: (id: number) => boolean,
  isFluid: (id: number) => boolean,
  /** May the dig take this ground or floor block? Omitted, any solid ground
   *  may be dug; the game passes `pitClearable`, which keeps a chest, a bed or
   *  a part of another house. */
  diggable: (id: number) => boolean = () => true,
  /** What may be cleared out of the way instead of refusing — `pitclear.ts`.
   *  Omitted, anything that is not air blocks the plot. */
  clearable: (id: number) => boolean = () => false,
): 'free' | 'blocked' | 'unlevel' | 'built' {
  for (let d = 0; d < DEPTH; d++)
    for (let w = -HALF_WIDTH; w <= HALF_WIDTH; w++)
      for (let h = 0; h <= ROOF_CLEAR; h++) {
        const c = cellAt(door, facing, d, w, h);
        const there = blockAt(c.x, c.y, c.z);
        if (there !== 0 && !clearable(there)) return 'blocked';
      }
  for (const c of pitGround(door, facing)) {
    const g = blockAt(c.x, c.y, c.z);
    if (g === 0 || !isSolid(g) || isFluid(g)) return 'unlevel';
  }
  for (const c of pitFloor(door, facing)) {
    for (const y of [c.y, c.y - 1]) {
      const g = blockAt(c.x, y, c.z);
      if (g === 0 || !isSolid(g) || isFluid(g)) return 'unlevel';
    }
  }
  /* 🟫 The dig destroys what is in a ground or floor cell, so nothing that
     holds things or belongs to another building may be there. */
  for (const c of [...pitGround(door, facing), ...pitFloor(door, facing)]) {
    if (!diggable(blockAt(c.x, c.y, c.z))) return 'built';
  }
  return 'free';
}

/**
 * 🧹 **Is the room empty of BLOCKS?** — a chest, a torch, a loom left inside
 * would be buried by the refill, so the house cannot come down until it is.
 * ⚠️ **Players and animals are the server's to check** — this sees blocks only.
 */
export function pitRoomIsEmpty(
  door: Cell,
  facing: BurFacing,
  blockAt: (x: number, y: number, z: number) => number,
): boolean {
  return pitRoom(door, facing).every((c) => blockAt(c.x, c.y, c.z) === 0);
}

/* ── the blocks ─────────────────────────────────────────────────────────── */

const idOf = (key: string): number | undefined => blockIdForKey(key);

/** 🚪 Is this — in any state — the pit-house door? */
export function isPitDoor(id: number): boolean {
  const base = idOf(PIT_DOOR);
  return base !== undefined && baseBlockOf(id) === base;
}

/** 🧱 Is this a wall/ceiling cell or the step — the parts that come down only at the door? */
export function isPitPart(id: number): boolean {
  return id !== 0 && (id === idOf(PIT_PART) || id === idOf(PIT_STEP) || id === idOf(PIT_FLOOR));
}

/** 🟫 Is this the pit-house floor — the one part the MESHER draws? */
export function isPitFloor(id: number): boolean {
  return id !== 0 && id === idOf(PIT_FLOOR);
}

/** 🔥 Is this the oven, lit or not? */
export function isPitOven(id: number): boolean {
  const base = idOf(PIT_OVEN);
  return base !== undefined && baseBlockOf(id) === base;
}

/** 🧵 Is this the bench? */
export function isPitBench(id: number): boolean {
  return id !== 0 && id === idOf(PIT_BENCH);
}

/** 🏠 Is this any block of a pit house — door, part, oven or bench? */
export function isPitBlock(id: number): boolean {
  return isPitDoor(id) || isPitPart(id) || isPitOven(id) || isPitBench(id);
}

/** 🧭 Which way a pit-house door faces, or `undefined` for anything else. */
export function pitFacing(id: number): BurFacing | undefined {
  if (!isPitDoor(id)) return undefined;
  const f = stateOf(id)['facing'];
  return typeof f === 'string' && (BUR_FACINGS as readonly string[]).includes(f)
    ? (f as BurFacing)
    : undefined;
}

/** 🚪 Is this door open? `false` for anything that is not one. */
export function pitDoorIsOpen(id: number): boolean {
  return isPitDoor(id) && stateOf(id)['open'] === true;
}

/** 🚪 Which half of the door this is, or `undefined` for anything else. */
export function pitDoorHalf(id: number): 'lower' | 'upper' | undefined {
  if (!isPitDoor(id)) return undefined;
  return stateOf(id)['half'] === 'upper' ? 'upper' : 'lower';
}

/** 🚪 The door block for a facing, open or shut, lower or upper half. */
export function pitDoorIdFor(
  facing: BurFacing,
  open: boolean,
  half: 'lower' | 'upper',
): number | undefined {
  const base = idOf(PIT_DOOR);
  return base === undefined ? undefined : withState(base, { facing, open, half });
}

/**
 * 🚪 **Which door answers for this cell** — the derivation run backwards, the
 * búr's `burDoorFor`. Returns the door's LOWER cell (the anchor), or `undefined`
 * for anything that is not part of a pit house.
 */
export function pitDoorFor(
  cell: Cell,
  blockAt: (x: number, y: number, z: number) => number,
): Cell | undefined {
  const here = blockAt(cell.x, cell.y, cell.z);
  /* A plain position — never the caller's object, which may carry a `role`. */
  if (isPitDoor(here))
    return { x: cell.x, y: pitDoorHalf(here) === 'upper' ? cell.y - 1 : cell.y, z: cell.z };
  if (!isPitBlock(here)) return undefined;
  for (const facing of BUR_FACINGS) {
    const { into, right } = burFrame(facing);
    for (let d = 0; d < DEPTH; d++)
      for (let w = -HALF_WIDTH; w <= HALF_WIDTH; w++)
        for (let h = -1; h <= CEILING + 1; h++) {
          const door = {
            x: cell.x - into.x * d - right.x * w,
            y: cell.y - h,
            z: cell.z - into.z * d - right.z * w,
          };
          const id = blockAt(door.x, door.y, door.z);
          if (isPitDoor(id) && pitDoorHalf(id) === 'lower' && pitFacing(id) === facing) return door;
        }
  }
  return undefined;
}

/**
 * 🔥 **Which number the oven should be, or `undefined` if it already agrees** —
 * `litStateFor`'s rule for the hearth, for the oven.
 *
 * 🔴 **It refuses anything that is not an oven**, and that is the lesson in
 * `litStateFor`'s own comment: a fire's lit-state write once overwrote a búr
 * with a hearth. ⚠️ Takes `burning` rather than a `HearthState`, so this file
 * never imports `hearth.ts`.
 */
export function pitOvenLitStateFor(current: number, burning: boolean): number | undefined {
  if (!isPitOven(current)) return undefined;
  const base = idOf(PIT_OVEN);
  if (base === undefined) return undefined;
  const wanted = withState(base, { lit: burning });
  if (wanted === undefined) return undefined;
  return wanted === current ? undefined : wanted;
}

/** 🧍 A body's box — the shape `crowd.ts` gives players and animals. */
interface BodyBox {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly half: number;
  readonly height: number;
}

/**
 * 🧍 **Is any of these bodies inside the house?**
 *
 * - `whole` — the 7 × 6 footprint from the pit floor to the roof (`h = −1…+3`).
 *   Placing over a body is refused, as a gate refuses to shut on one.
 * - `room` — the 5 × 4 room from the pit floor to the ceiling (`h = −1…+1`).
 *   🔴 Taking the house down with somebody in it would bury them in the refill.
 */
export function pitBodyInside(
  door: Cell,
  facing: BurFacing,
  part: 'whole' | 'room',
  bodies: readonly BodyBox[],
): boolean {
  const [d0, d1, w0, w1, h0, h1] =
    part === 'whole'
      ? [0, DEPTH - 1, -HALF_WIDTH, HALF_WIDTH, -1, ROOF_CLEAR]
      : [1, DEPTH - 2, -HALF_WIDTH + 1, HALF_WIDTH - 1, -1, WALL_TOP];
  const a = cellAt(door, facing, d0, w0, h0);
  const b = cellAt(door, facing, d1, w1, h1);
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x) + 1;
  const minZ = Math.min(a.z, b.z);
  const maxZ = Math.max(a.z, b.z) + 1;
  const minY = a.y;
  const maxY = b.y + 1;
  return bodies.some(
    (o) =>
      o.x + o.half > minX &&
      o.x - o.half < maxX &&
      o.z + o.half > minZ &&
      o.z - o.half < maxZ &&
      o.y < maxY &&
      o.y + o.height > minY,
  );
}

/** 🧵 **The bench's own cell** — the end nearest the door. Its spinning lives here. */
export function pitBenchCell(door: Cell, facing: BurFacing): Cell {
  return cellAt(door, facing, 1, 2, -1);
}

/**
 * 🧵 **Every door a bench cell could belong to** — 16 candidates, so the server
 * can find the house with 16 reads rather than {@link pitDoorFor}'s hundreds.
 * The caller keeps the one whose cell holds a lower door with that facing.
 */
export function pitBenchDoors(bench: Cell): { door: Cell; facing: BurFacing }[] {
  const out: { door: Cell; facing: BurFacing }[] = [];
  for (const facing of BUR_FACINGS) {
    const { into, right } = burFrame(facing);
    for (let d = 1; d <= 4; d++)
      out.push({
        facing,
        door: {
          x: bench.x - into.x * d - right.x * 2,
          y: bench.y + 1,
          z: bench.z - into.z * d - right.z * 2,
        },
      });
  }
  return out;
}
